import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {
  Alert,
  Avatar,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import {
  FaBook,
  FaEdit,
  FaEnvelope,
  FaFacebook,
  FaLinkedin,
  FaList,
  FaMapPin,
  FaPhone,
  FaSave,
  FaTimes,
  FaTwitter,
  FaHome,
} from "react-icons/fa";
import { IoMdNotifications, IoMdSettings } from "react-icons/io";
import { IoRefresh } from "react-icons/io5";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Context/AuthContext";
import Navbar from "../components/Navbar";
import CountryPhoneInput from "../components/CountryPhoneInput";
import axios from "axios";
import { toast } from "react-toastify";

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser, isAuthenticated } = React.useContext(AuthContext);
  const [isEditing, setIsEditing] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const [showPreferences, setShowPreferences] = React.useState(true);
  const [notificationDialogOpen, setNotificationDialogOpen] = React.useState(false);
  
  // User preferences state - Initialize with null values to indicate "loading" state
  const [preferences, setPreferences] = React.useState({
    emailNotification: false,
    smsNotification: false,
    whatsappNotification: false,
  });
  const [loadingToggles, setLoadingToggles] = React.useState({
    emailNotification: false,
    smsNotification: false,
    whatsappNotification: false,
  });
  const [preferenceError, setPreferenceError] = React.useState(null);
  const [fetchLoading, setFetchLoading] = React.useState(true); // <-- Set to true initially
  const [refreshing, setRefreshing] = React.useState(false);
  const [notificationChannels, setNotificationChannels] = React.useState([
    { key: "emailNotification", label: "Email" },
    { key: "smsNotification", label: "SMS" },
    { key: "whatsappNotification", label: "WhatsApp" },
  ]);
  
  // Form state
  const [formData, setFormData] = React.useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: user?.address || "",
  });

  // Add state for friends list
  const [emails, setEmails] = React.useState([]);
  const [txtEmail, setTxtEmail] = React.useState('');
  const [loadingFriends, setLoadingFriends] = React.useState(false);
  const [showFriendsList, setShowFriendsList] = React.useState(false);
  const [friendError, setFriendError] = React.useState(null);

  // Add state for doctors list
  const [doctors, setDoctors] = React.useState([]);
  const [newDoctor, setNewDoctor] = React.useState({ name: '', email: '', specialty: '' });
  const [loadingDoctors, setLoadingDoctors] = React.useState(false);
  const [showDoctorsList, setShowDoctorsList] = React.useState(false);
  const [doctorError, setDoctorError] = React.useState(null);

  // Add state for insurance list
  const [insurance, setInsurance] = React.useState([]);
  const [newInsurance, setNewInsurance] = React.useState({ name: '', email: '', type: '' });
  const [loadingInsurance, setLoadingInsurance] = React.useState(false);
  const [showInsuranceList, setShowInsuranceList] = React.useState(false);
  const [insuranceError, setInsuranceError] = React.useState(null);

  // Create a custom axios instance that doesn't redirect on 401
  const axiosInstance = axios.create();
  // Override the default behavior to avoid redirection on 401 errors
  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status === 401) {
        // Handle 401 error without signing out
        setPreferenceError("Please refresh the page and login again to manage notification preferences");
      }
      return Promise.reject(error);
    }
  );

  // Fetch user preferences
  const fetchPreferences = React.useCallback(async () => {
    if (!isAuthenticated) {
      setPreferenceError("User not authenticated");
      setFetchLoading(false);
      return;
    }
    
    setRefreshing(true);
    setFetchLoading(true); // Set loading state when fetching
    
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setPreferenceError("Authentication token not found");
        setRefreshing(false);
    setFetchLoading(false);
        return;
      }
      
      console.log("Fetching preferences with token:", token);
      
      // Use regular axios instead of axiosInstance for better error handling
      const response = await axios.get(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/preferences/get`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          // Don't auto-redirect on authentication errors
          validateStatus: status => status < 500
        }
      );

      console.log("Preferences API response:", response);

      if (response.status === 401) {
        // Handle 401 error explicitly
        console.log("Authentication error when fetching preferences");
        setPreferenceError("Authentication error. Please refresh and try again.");
        setFetchLoading(false);
        return;
      }

      if (response.data && response.data.success) {
        setPreferences({
          emailNotification: Boolean(response.data.emailNotification),
          smsNotification: Boolean(response.data.smsNotification),
          whatsappNotification: Boolean(response.data.whatsappNotification),
        });
        if (Array.isArray(response.data.channels) && response.data.channels.length) {
          setNotificationChannels(response.data.channels);
        }
        // Add debug logging to see the returned preferences
        console.log("Parsed preferences:", {
          emailNotification: Boolean(response.data.emailNotification),
          smsNotification: Boolean(response.data.smsNotification),
          whatsappNotification: Boolean(response.data.whatsappNotification),
        });
        setPreferenceError(null);
      } else {
        console.log("Preferences API response had success: false", response.data);
        setPreferenceError("Could not load preferences. Please try again later.");
      }
    } catch (err) {
      console.error("Error fetching preferences:", err);
      // Don't show toast for network errors to avoid overwhelming the user
      setPreferenceError("Could not load preferences. Please try again later.");
    } finally {
      setRefreshing(false);
      setFetchLoading(false);
    }
  }, [isAuthenticated]);

  // Handle preference toggle with immediate update
  const handleToggle = async (name) => {
    // Update loading state for the specific toggle
    setLoadingToggles(prev => ({
      ...prev,
      [name]: true
    }));

    // Optimistic UI update
    const newValue = !preferences[name];
    setPreferences(prev => ({
      ...prev,
      [name]: newValue
    }));

    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        throw new Error("Authentication token not found");
      }
      
      console.log(`Updating preference ${name} to ${newValue}`);
      
      // Create a modified update object that maps frontend field name to backend field name
      // Specifically handle the case where whatsappNotification in frontend maps to whatsAppNotification in backend
      const updateData = { 
        emailNotification: preferences.emailNotification,
        smsNotification: preferences.smsNotification,
        whatsAppNotification: name === 'whatsappNotification' ? newValue : preferences.whatsappNotification
      };
      
      if (name !== 'whatsappNotification') {
        // Only update the other preference if it's not the whatsapp one
        updateData[name] = newValue;
      }
      
      console.log("Sending preference update:", updateData);
      
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/preferences/update`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          // Don't auto-redirect on authentication errors
          validateStatus: status => status < 500
        }
      );
      
      console.log("Preference update response:", response);
      
      if (response.status === 401) {
        // Handle 401 error explicitly
        throw new Error("Authentication error");
      }

      if (response.data.success) {
        // Show success message with proper capitalization; SMS uppercase
        const label = name === 'emailNotification' ? 'Email' : name === 'smsNotification' ? 'SMS' : 'WhatsApp';
        toast.success(`${label} notifications ${newValue ? 'enabled' : 'disabled'}`);
        setPreferenceError(null);
      } else {
        throw new Error("Server returned success: false");
      }
    } catch (err) {
      console.error("Error updating preferences:", err);
      
      // Revert the optimistic update
      setPreferences(prev => ({
        ...prev,
        [name]: !newValue
      }));
      
      // Show error message
      if (err.message === "Authentication error") {
        setPreferenceError("Authentication error. Please refresh the page and login again.");
      } else {
        const label = name === 'emailNotification' ? 'Email' : name === 'smsNotification' ? 'SMS' : 'WhatsApp';
        toast.error(`Failed to update ${label} preference`);
      }
    } finally {
      // Reset loading state
      setLoadingToggles(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  // Update form data when user changes
  React.useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
      });
    }
    
    // Fetch preferences when component mounts
    if (isAuthenticated) {
      // Make sure to fetch preferences only once when authenticated
      fetchPreferences();
    }
  }, [user, isAuthenticated, fetchPreferences]);
  
  const goto = (path) => {
    navigate(`/${path}`);
    scrollTo({top: 0});
  };

  // Handle notifications click
  const handleNotificationsClick = () => {
    // Instead of navigating, open the dialog
    setNotificationDialogOpen(true);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    if (isEditing) {
      // Reset form data if cancelling
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
      });
    }
    setIsEditing(!isEditing);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      // Log the request details for debugging
      console.log("[Profile Update] Phone with country code:", formData.phone);
      console.log("Updating profile with data:", formData);
      console.log("API URL:", `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/patient`);
      
      // Use the correct endpoint based on your backend API
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/patient`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Update local storage and context
        const updatedUser = {...user, ...formData};
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        
        setSnackbar({
          open: true,
          message: "Profile updated successfully!",
          severity: "success"
        });
        toast.success("Profile updated successfully!");
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Update error:", err);
      const errorMessage = err.response?.data?.message || "Failed to update profile";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error"
      });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Define menu options for the sidebar
  const menuOptions = [
    { icon: <FaEnvelope />, label: "Email", value: user?.email, action: null },
    { icon: <FaPhone />, label: "Phone", value: user?.phone, action: null },
    { icon: <FaHome />, label: "Address", value: user?.address, action: null },
    { icon: <FaMapPin />, label: "Location", action: () => goto('location') },
    { icon: <IoMdSettings />, label: "Settings", action: () => goto('settings') },
    { icon: <IoMdNotifications />, label: "Notification", action: handleNotificationsClick },
    { icon: <FaList />, label: "Friends list", action: () => goto('friends-list') },
    { icon: <FaList />, label: "Doctors List", action: () => goto('doctors-list') },
    { icon: <FaBook />, label: "Insurance List", action: () => goto('insurance-list') },
  ];

  // Add a function to toggle preferences visibility
  const togglePreferences = () => {
    setShowPreferences(!showPreferences);
  };

  // Fetch friends list
  const getEmailList = async () => {
    if (!isAuthenticated) {
      setFriendError("User not authenticated");
      setLoadingFriends(false);
      return;
    }
    
    setLoadingFriends(true);
    setFriendError(null);
    
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setFriendError("Authentication token not found");
        setLoadingFriends(false);
        return;
      }
      
      console.log("Fetching friends list with token:", token);
      
      const response = await axios.get(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam/friend`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          validateStatus: status => status < 500
        }
      );
      
      console.log("Friends list API response:", response);
      
      if (response.status === 401) {
        console.log("Authentication error when fetching friends");
        setFriendError("Authentication error. Please refresh and try again.");
        setLoadingFriends(false);
        return;
      }
      
      setEmails(response.data || []);
    } catch (err) {
      console.error("Error fetching friends:", err);
      setFriendError("Could not load friends list. Please try again later.");
    } finally {
      setLoadingFriends(false);
    }
  };

  // Add email to friends list
  const addEmail = async (emailAddress) => {
    if (!emailAddress || !emailAddress.trim()) {
      toast.warning("Please enter a valid email address");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setFriendError("Authentication token not found");
        return;
      }
      
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam`,
        { 
          emailAddress, 
          personType: 'friend' 
        },
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        }
      );
      
      console.log("Add friend response:", response);
      
      if (response.status === 200 && response.data.emailRecord) {
        setEmails([...emails, response.data.emailRecord]);
        toast.success("Friend added successfully");
        setTxtEmail('');
      }
    } catch (error) {
      console.error("Error adding friend:", error);
      toast.error(error.response?.data?.message || "Failed to add friend");
    }
  };

  // Remove email from friends list
  const removeEmail = async (emailItem) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setFriendError("Authentication token not found");
        return;
      }
      
      const response = await axios.delete(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam/${emailItem._id}`,
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        }
      );
      
      console.log("Remove friend response:", response);
      
      if (response.status === 200) {
        setEmails(emails.filter(item => item._id !== emailItem._id));
        toast.success("Friend removed successfully");
      }
    } catch (error) {
      console.error("Error removing friend:", error);
      toast.error(error.response?.data?.message || "Failed to remove friend");
    }
  };
  
  // Toggle friends list visibility
  const toggleFriendsList = () => {
    const newState = !showFriendsList;
    setShowFriendsList(newState);
    if (newState && emails.length === 0) {
      getEmailList();
    }
  };

  // Doctor Management Functions
  const getDoctorsList = async () => {
    if (!isAuthenticated) {
      setDoctorError("User not authenticated");
      setLoadingDoctors(false);
      return;
    }
    
    setLoadingDoctors(true);
    setDoctorError(null);
    
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setDoctorError("Authentication token not found");
        setLoadingDoctors(false);
        return;
      }
      
      const response = await axios.get(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam/doctor`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          validateStatus: status => status < 500
        }
      );
      
      if (response.status === 401) {
        setDoctorError("Authentication error. Please refresh and try again.");
        setLoadingDoctors(false);
        return;
      }
      
      setDoctors(response.data || []);
    } catch (err) {
      console.error("Error fetching doctors:", err);
      setDoctorError("Could not load doctors list. Please try again later.");
    } finally {
      setLoadingDoctors(false);
    }
  };

  const addDoctor = async () => {
    if (!newDoctor.email) {
      toast.warning("Please enter doctor email");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setDoctorError("Authentication token not found");
        return;
      }
      
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam`,
        { 
          name: newDoctor.name,
          emailAddress: newDoctor.email,
          specialty: newDoctor.specialty,
          personType: 'doctor' 
        },
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        }
      );
      
      if (response.status === 200 && response.data.emailRecord) {
        setDoctors([...doctors, response.data.emailRecord]);
        toast.success("Doctor added successfully");
        setNewDoctor({ name: '', email: '', specialty: '' });
      }
    } catch (error) {
      console.error("Error adding doctor:", error);
      toast.error(error.response?.data?.message || "Failed to add doctor");
    }
  };

  const removeDoctor = async (doctorItem) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setDoctorError("Authentication token not found");
        return;
      }
      
      const response = await axios.delete(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam/${doctorItem._id}`,
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        }
      );
      
      if (response.status === 200) {
        setDoctors(doctors.filter(item => item._id !== doctorItem._id));
        toast.success("Doctor removed successfully");
      }
    } catch (error) {
      console.error("Error removing doctor:", error);
      toast.error(error.response?.data?.message || "Failed to remove doctor");
    }
  };

  const toggleDoctorsList = () => {
    const newState = !showDoctorsList;
    setShowDoctorsList(newState);
    if (newState && doctors.length === 0) {
      getDoctorsList();
    }
  };

  // Insurance Management Functions
  const getInsuranceList = async () => {
    if (!isAuthenticated) {
      setInsuranceError("User not authenticated");
      setLoadingInsurance(false);
      return;
    }
    
    setLoadingInsurance(true);
    setInsuranceError(null);
    
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setInsuranceError("Authentication token not found");
        setLoadingInsurance(false);
        return;
      }
      
      const response = await axios.get(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam/insurance`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          validateStatus: status => status < 500
        }
      );
      
      if (response.status === 401) {
        setInsuranceError("Authentication error. Please refresh and try again.");
        setLoadingInsurance(false);
        return;
      }
      
      setInsurance(response.data || []);
    } catch (err) {
      console.error("Error fetching insurance:", err);
      setInsuranceError("Could not load insurance list. Please try again later.");
    } finally {
      setLoadingInsurance(false);
    }
  };

  const addInsurance = async () => {
    if (!newInsurance.email) {
      toast.warning("Please enter insurance email");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setInsuranceError("Authentication token not found");
        return;
      }
      
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam`,
        { 
          name: newInsurance.name,
          emailAddress: newInsurance.email,
          insuranceType: newInsurance.type,
          personType: 'insurance' 
        },
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        }
      );
      
      if (response.status === 200 && response.data.emailRecord) {
        setInsurance([...insurance, response.data.emailRecord]);
        toast.success("Insurance added successfully");
        setNewInsurance({ name: '', email: '', type: '' });
      }
    } catch (error) {
      console.error("Error adding insurance:", error);
      toast.error(error.response?.data?.message || "Failed to add insurance");
    }
  };

  const removeInsurance = async (insuranceItem) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setInsuranceError("Authentication token not found");
        return;
      }
      
      const response = await axios.delete(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam/${insuranceItem._id}`,
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        }
      );
      
      if (response.status === 200) {
        setInsurance(insurance.filter(item => item._id !== insuranceItem._id));
        toast.success("Insurance removed successfully");
      }
    } catch (error) {
      console.error("Error removing insurance:", error);
      toast.error(error.response?.data?.message || "Failed to remove insurance");
    }
  };

  const toggleInsuranceList = () => {
    const newState = !showInsuranceList;
    setShowInsuranceList(newState);
    if (newState && insurance.length === 0) {
      getInsuranceList();
    }
  };

  return (
    <>
      <Navbar />
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Profile Header */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    alt={user?.firstName || "User"} 
                    src="/public/signin.png" 
                    sx={{ width: 80, height: 80, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h4" fontWeight="600">
                      {isEditing ? `${formData.firstName} ${formData.lastName}` : `${user?.firstName} ${user?.lastName}`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                        Verified with:
                      </Typography>
                      <FaFacebook style={{ marginRight: '8px', color: '#1877F2' }} />
                      <FaTwitter style={{ marginRight: '8px', color: '#1DA1F2' }} />
                      <FaLinkedin style={{ color: '#0A66C2' }} />
                    </Box>
                  </Box>
                </Box>
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={toggleEditMode}
                      startIcon={<FaTimes />}
                      disabled={loading}
                      sx={{ borderRadius: 2 }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      color="success"
                      onClick={handleSubmit}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FaSave />}
                      disabled={loading}
                      sx={{ borderRadius: 2 }}
                    >
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={toggleEditMode}
                    startIcon={<FaEdit />}
                    sx={{ borderRadius: 2 }}
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Settings UI - Sidebar & Content */}
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Typography 
                variant="h6" 
                sx={{ px: 2, py: 1.5, bgcolor: 'primary.light', color: 'white' }}
              >
                Profile Settings
              </Typography>
              <List sx={{ p: 0 }}>
                {menuOptions.map((option, index) => (
                  <React.Fragment key={index}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={option.action} sx={{ py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                          {option.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={option.label} 
                          secondary={isEditing && option.label === "Phone" ? formData.phone : 
                                    isEditing && option.label === "Address" ? formData.address : 
                                    option.value}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < menuOptions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>

            {/* Care Team Management Sections */}
            <Box sx={{ mt: 3 }}>
              {/* Buttons Row */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Friends Button + Mobile Content */}
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={toggleFriendsList}
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    {showFriendsList ? "Hide Friends List" : "Show Friends List"}
                  </Button>
                  {/* Mobile inline expansion (xs only) */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
                    {isAuthenticated && showFriendsList && (
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Friends List
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {friendError ? (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {friendError}
                            <Button
                              variant="text"
                              color="inherit"
                              onClick={getEmailList}
                              sx={{ ml: 2 }}
                              size="small"
                            >
                              Retry
                            </Button>
                          </Alert>
                        ) : loadingFriends ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                label="Friend's Email"
                                type="email"
                                value={txtEmail}
                                onChange={(e) => setTxtEmail(e.target.value)}
                                fullWidth
                                placeholder="Enter friend's email"
                                variant="outlined"
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => addEmail(txtEmail)}
                                startIcon={<AddIcon />}
                                size="small"
                              >
                                Add Friend
                              </Button>
                            </Box>
                            {emails.length > 0 ? (
                              emails.map((email) => (
                                <Card key={email._id || email.emailAddress} sx={{ mb: 1 }}>
                                  <CardContent sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
                                        {email.emailAddress}
                                      </Typography>
                                      <IconButton onClick={() => removeEmail(email)} color="error" size="small">
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Typography color="text.secondary" variant="body2">
                                  No friends added yet.
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Paper>
                    )}
                  </Box>
                </Grid>

                {/* Doctors Button + Mobile Content */}
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={toggleDoctorsList}
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    {showDoctorsList ? "Hide Doctors List" : "Show Doctors List"}
                  </Button>
                  {/* Mobile inline expansion (xs only) */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
                    {isAuthenticated && showDoctorsList && (
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Doctors List
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {doctorError ? (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {doctorError}
                            <Button
                              variant="text"
                              color="inherit"
                              onClick={getDoctorsList}
                              sx={{ ml: 2 }}
                              size="small"
                            >
                              Retry
                            </Button>
                          </Alert>
                        ) : loadingDoctors ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                label="Doctor Email"
                                type="email"
                                value={newDoctor.email}
                                onChange={(e) => setNewDoctor({ ...newDoctor, email: e.target.value })}
                                fullWidth
                                placeholder="Enter doctor's email"
                                variant="outlined"
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={addDoctor}
                                startIcon={<AddIcon />}
                                color="secondary"
                                size="small"
                              >
                                Add Doctor
                              </Button>
                            </Box>
                            {doctors.length > 0 ? (
                              doctors.map((doctor) => (
                                <Card key={doctor._id} sx={{ mb: 1 }}>
                                  <CardContent sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
                                          {doctor.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          {doctor.emailAddress}
                                        </Typography>
                                        {doctor.specialty && (
                                          <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                            {doctor.specialty}
                                          </Typography>
                                        )}
                                      </Box>
                                      <IconButton onClick={() => removeDoctor(doctor)} color="error" size="small">
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Typography color="text.secondary" variant="body2">
                                  No doctors added yet.
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Paper>
                    )}
                  </Box>
                </Grid>

                {/* Insurance Button + Mobile Content */}
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={toggleInsuranceList}
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    {showInsuranceList ? "Hide Insurance List" : "Show Insurance List"}
                  </Button>
                  {/* Mobile inline expansion (xs only) */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
                    {isAuthenticated && showInsuranceList && (
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Insurance List
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {insuranceError ? (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {insuranceError}
                            <Button
                              variant="text"
                              color="inherit"
                              onClick={getInsuranceList}
                              sx={{ ml: 2 }}
                              size="small"
                            >
                              Retry
                            </Button>
                          </Alert>
                        ) : loadingInsurance ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                label="Insurance Email"
                                type="email"
                                value={newInsurance.email}
                                onChange={(e) => setNewInsurance({ ...newInsurance, email: e.target.value })}
                                fullWidth
                                placeholder="Enter insurance email"
                                variant="outlined"
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={addInsurance}
                                startIcon={<AddIcon />}
                                color="success"
                                size="small"
                              >
                                Add Insurance
                              </Button>
                            </Box>
                            {insurance.length > 0 ? (
                              insurance.map((insuranceItem) => (
                                <Card key={insuranceItem._id} sx={{ mb: 1 }}>
                                  <CardContent sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
                                          {insuranceItem.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          {insuranceItem.emailAddress}
                                        </Typography>
                                        {insuranceItem.insuranceType && (
                                          <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                            {insuranceItem.insuranceType}
                                          </Typography>
                                        )}
                                      </Box>
                                      <IconButton onClick={() => removeInsurance(insuranceItem)} color="error" size="small">
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Typography color="text.secondary" variant="body2">
                                  No insurance added yet.
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Paper>
                    )}
                  </Box>
                </Grid>
              </Grid>
              {/* Desktop full-width expansions (md and up) */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {isAuthenticated && showFriendsList && (
                  <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Friends List
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {friendError ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {friendError}
                        <Button variant="text" color="inherit" onClick={getEmailList} sx={{ ml: 2 }} size="small">
                          Retry
                        </Button>
                      </Alert>
                    ) : loadingFriends ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            label="Friend's Email"
                            type="email"
                            value={txtEmail}
                            onChange={(e) => setTxtEmail(e.target.value)}
                            fullWidth
                            placeholder="Enter friend's email"
                            variant="outlined"
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <Button variant="contained" fullWidth onClick={() => addEmail(txtEmail)} startIcon={<AddIcon />} size="small">
                            Add Friend
                          </Button>
                        </Box>
                        {emails.length > 0 ? (
                          emails.map((email) => (
                            <Card key={email._id || email.emailAddress} sx={{ mb: 1 }}>
                              <CardContent sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.9rem' }}>
                                    {email.emailAddress}
                                  </Typography>
                                  <IconButton onClick={() => removeEmail(email)} color="error" size="small">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                              No friends added yet.
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                )}

                {isAuthenticated && showDoctorsList && (
                  <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Doctors List
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {doctorError ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {doctorError}
                        <Button variant="text" color="inherit" onClick={getDoctorsList} sx={{ ml: 2 }} size="small">
                          Retry
                        </Button>
                      </Alert>
                    ) : loadingDoctors ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            label="Doctor Email"
                            type="email"
                            value={newDoctor.email}
                            onChange={(e) => setNewDoctor({ ...newDoctor, email: e.target.value })}
                            fullWidth
                            placeholder="Enter doctor's email"
                            variant="outlined"
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <Button variant="contained" fullWidth onClick={addDoctor} startIcon={<AddIcon />} color="secondary" size="small">
                            Add Doctor
                          </Button>
                        </Box>
                        {doctors.length > 0 ? (
                          doctors.map((doctor) => (
                            <Card key={doctor._id} sx={{ mb: 1 }}>
                              <CardContent sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.9rem' }}>
                                      {doctor.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {doctor.emailAddress}
                                    </Typography>
                                    {doctor.specialty && (
                                      <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                        {doctor.specialty}
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton onClick={() => removeDoctor(doctor)} color="error" size="small">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                              No doctors added yet.
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                )}

                {isAuthenticated && showInsuranceList && (
                  <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Insurance List
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {insuranceError ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {insuranceError}
                        <Button variant="text" color="inherit" onClick={getInsuranceList} sx={{ ml: 2 }} size="small">
                          Retry
                        </Button>
                      </Alert>
                    ) : loadingInsurance ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            label="Insurance Email"
                            type="email"
                            value={newInsurance.email}
                            onChange={(e) => setNewInsurance({ ...newInsurance, email: e.target.value })}
                            fullWidth
                            placeholder="Enter insurance email"
                            variant="outlined"
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <Button variant="contained" fullWidth onClick={addInsurance} startIcon={<AddIcon />} color="success" size="small">
                            Add Insurance
                          </Button>
                        </Box>
                        {insurance.length > 0 ? (
                          insurance.map((insuranceItem) => (
                            <Card key={insuranceItem._id} sx={{ mb: 1 }}>
                              <CardContent sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.9rem' }}>
                                      {insuranceItem.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {insuranceItem.emailAddress}
                                    </Typography>
                                    {insuranceItem.insuranceType && (
                                      <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                        {insuranceItem.insuranceType}
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton onClick={() => removeInsurance(insuranceItem)} color="error" size="small">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                              No insurance added yet.
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                )}
              </Box>
            </Box>
            </Paper>

            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {isEditing ? "Edit Account Information" : "Account Information"}
              </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {isEditing ? (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      variant="outlined"
                      disabled // Email should not be editable
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CountryPhoneInput
                      value={formData.phone}
                      onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                      label="Phone"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      variant="outlined"
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{user?.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{user?.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body1">
                    {new Date(user?.createdAt || Date.now()).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {user?.address || 'Not provided'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
          
          {/* Notification Preferences - Moved from separate component */}
          {isAuthenticated && showPreferences && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Notification Preferences
                </Typography>
                <Tooltip title="Refresh preferences">
                  <IconButton 
                    onClick={fetchPreferences} 
                    disabled={refreshing}
                    color="primary"
                    sx={{ '&:hover': { transform: 'rotate(180deg)', transition: 'transform 0.5s' } }}
                  >
                    {refreshing ? <CircularProgress size={20} /> : <IoRefresh />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {preferenceError ? (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {preferenceError}
                  </Alert>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={fetchPreferences}
                    disabled={refreshing}
                    startIcon={refreshing ? <CircularProgress size={16} /> : <IoRefresh />}
                    sx={{ mt: 1 }}
                  >
                    Try Again
                  </Button>
                </Box>
              ) : fetchLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <FormGroup>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {notificationChannels.map((channel) => (
                      <Chip
                        key={channel.key}
                        label={channel.label}
                        color={preferences[channel.key] ? "primary" : "default"}
                        variant={preferences[channel.key] ? "filled" : "outlined"}
                        size="small"
                      />
                    ))}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={Boolean(preferences.emailNotification)}
                        onChange={() => handleToggle('emailNotification')}
                        disabled={loadingToggles.emailNotification}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Email notifications
                        {loadingToggles.emailNotification && (
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={Boolean(preferences.smsNotification)}
                        onChange={() => handleToggle('smsNotification')}
                        disabled={loadingToggles.smsNotification}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        SMS notifications
                        {loadingToggles.smsNotification && (
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={Boolean(preferences.whatsappNotification)}
                        onChange={() => handleToggle('whatsappNotification')}
                        disabled={loadingToggles.whatsappNotification}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        WhatsApp notifications
                        {loadingToggles.whatsappNotification && (
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                      </Box>
                    }
                  />
                </FormGroup>
              )}
            </Paper>
          )}
          
          {/* Button to toggle preferences visibility if there are errors */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={togglePreferences}
              sx={{ borderRadius: 2 }}
            >
              {showPreferences ? "Hide Notification Settings" : "Show Notification Settings"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
    
    {/* Notification Dialog */}
    <Dialog
      open={notificationDialogOpen}
      onClose={() => setNotificationDialogOpen(false)}
      aria-labelledby="notification-dialog-title"
      aria-describedby="notification-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="notification-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IoMdNotifications style={{ marginRight: '8px', fontSize: '24px', color: '#1976d2' }} />
          Notifications
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="notification-dialog-description">
          <Box sx={{ mb: 2 }}>
            You don&apos;t have any new notifications at the moment.
          </Box>
          <Typography variant="body2" color="text.secondary">
            We&apos;ll notify you here when there are updates about your appointments, 
            medication reminders, or messages from your healthcare providers.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNotificationDialogOpen(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
    
    <Snackbar 
      open={snackbar.open} 
      autoHideDuration={6000} 
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleCloseSnackbar} 
        severity={snackbar.severity} 
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
);
}
